import { createReducer, on } from '@ngrx/store';
import * as MemberActions from './member.actions';
import { logout } from '@auth/store/auth.actions';
import { State } from '@store/state.interface';
import { MemberDto } from '@shared/dto';

export const MEMBER_FEATURE_KEY = 'members';

export interface MemberState extends State<MemberDto> {}

export const initialState: MemberState = {
  data: null,
  detail: null,
  loading: false,
  total: 0,
  query: null,
  error: null,
};

export const memberReducer = createReducer(
  initialState,
  on(MemberActions.readMembers, (state, { deleted }) => ({
    ...state,
    loading: true,
    error: null,
    query: deleted ? { deleted: true } : null,
  })),
  on(MemberActions.readMembersSuccess, (state, { data }) => ({ ...state, data, loading: false })),
  on(MemberActions.readMembersFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Detail (single entity): the resolver blocks activation and matches by id, so we
  // keep the previous detail during the load (no blank flash on the outgoing page).
  on(MemberActions.readMember, (state) => ({ ...state, loading: true, error: null })),
  on(MemberActions.readMemberSuccess, (state, { member }) => ({ ...state, detail: member, loading: false })),
  on(MemberActions.readMemberFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Keep the open detail in sync with mutations so the form/restore-banner stay
  // correct without a refetch (the list itself is refetched by reloadAfterMutation$).
  on(MemberActions.updateMemberSuccess, (state, { member }) => ({
    ...state,
    // A save can complete after the open profile has changed (route reuse on `:id`).
    detail: state.detail && (member.id == null || member.id === state.detail.id)
      ? { ...state.detail, ...member }
      : state.detail,
  })),
  // Same route-reuse hazard as updateMemberSuccess above: a late delete/restore
  // for a member the practitioner has since navigated away from must not
  // clobber the currently-open member's detail.
  on(MemberActions.deleteMemberSuccess, (state, { member }) => ({
    ...state,
    detail: state.detail && member.id === state.detail.id ? member : state.detail,
  })),
  on(MemberActions.restoreMemberSuccess, (state, { member }) => ({
    ...state,
    detail: state.detail && member.id === state.detail.id ? member : state.detail,
  })),
  on(logout, () => initialState),
);
